import { queryOptions } from '@tanstack/react-query';

import { EQueryKeys } from '@enums/queryKeys.enum';
import { medicationImportFileSchema } from '@schemas/medicationImportFile.schema';
import { medicationUpdatesService } from '@services/medicationUpdates/service';
import type { GetMedicationUpdateByIdOutput } from '@services/medicationUpdates/types/getById.type';

import type { ContentEditorSettings } from './types';

export const medicationUpdateEditorSettings = {
  api: {
    createMessage: ({ demandId, message }) =>
      medicationUpdatesService.createMessage({
        medicationUpdateId: demandId,
        message,
      }),
    createTopic: ({ content, demandId, position, title }) =>
      medicationUpdatesService.createTopic({
        content,
        medicationUpdateId: demandId,
        position,
        title,
      }),
    importTopics: ({ demandId, file }) =>
      medicationUpdatesService.importTopics({
        file,
        medicationUpdateId: demandId,
      }),
    markReview: demandId => medicationUpdatesService.markReview(demandId),
    removeAllTopics: demandId =>
      medicationUpdatesService.removeAllTopics({
        medicationUpdateId: demandId,
      }),
    removeTopic: ({ demandId, id }) =>
      medicationUpdatesService.removeTopic({
        id,
        medicationUpdateId: demandId,
      }),
    reorderTopics: ({ demandId, ids }) =>
      medicationUpdatesService.reorderTopics({
        ids,
        medicationUpdateId: demandId,
      }),
    updateMessage: ({ demandId, message, messageId }) =>
      medicationUpdatesService.updateMessage({
        medicationUpdateId: demandId,
        medicationUpdateMessageId: messageId,
        message,
      }),
    updateTopic: ({ content, demandId, id, title }) =>
      medicationUpdatesService.updateTopic({
        content,
        id,
        medicationUpdateId: demandId,
        title,
      }),
  },
  bulas: {
    create: ({ demandId, ...params }) =>
      medicationUpdatesService.createBula({
        ...params,
        medicationUpdateId: demandId,
      }),
    remove: ({ demandId, id }) =>
      medicationUpdatesService.removeBula({
        id,
        medicationUpdateId: demandId,
      }),
    reorder: ({ demandId, ids }) =>
      medicationUpdatesService.reorderBulas({
        ids,
        medicationUpdateId: demandId,
      }),
    update: ({ demandId, ...params }) =>
      medicationUpdatesService.updateBula({
        ...params,
        medicationUpdateId: demandId,
      }),
    updateFile: ({ demandId, file, id }) =>
      medicationUpdatesService.updateBulaFile({
        file,
        id,
        medicationUpdateId: demandId,
      }),
  },
  detailsQueryOptions: (id: string) =>
    queryOptions({
      queryKey: [EQueryKeys.MedicationDetails, id],
      queryFn: () => medicationUpdatesService.getById(id),
      select: data => data.medication,
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    }),
  import: {
    confirmationNote:
      'Bulas com o mesmo número de referência serão substituídas.',
    fileSchema: medicationImportFileSchema,
  },
  labels: {
    noun: 'medicamento',
    title: 'Detalhes do medicamento',
  },
  listPath: '/my-medications',
  page: '/new-medications',
  refreshDetails: queryClient =>
    queryClient.invalidateQueries({
      queryKey: [EQueryKeys.MedicationDetails],
    }),
  reviewQueryKeys: [
    [EQueryKeys.MedicationDetails],
    [EQueryKeys.MyMedicationUpdates],
    [EQueryKeys.MyMedicationCreates],
    [EQueryKeys.Medications],
  ],
} satisfies ContentEditorSettings<GetMedicationUpdateByIdOutput>;
